import * as Yup from 'yup';
import { Col, Row, Space, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { FormikProvider, useFormik } from 'formik';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import FormNumberInput from 'components/new-ui/composed/formInput/FormNumberInput';
import { isBeValidationError } from 'utils/common';
import Notification from 'components/ui/base/notification/Notification';
import { getErrorMessage } from 'utils/getErrorMessage';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';

type FormValues = { amount: number; percentage: number | null };

type Props = {
    open: boolean;
    currencySymbol: string;
    initialData?: { amount: number; percentage?: number | null };
    onClose: () => void;
    onConfirm: (values: FormValues) => Promise<void>;
};

enum FormFiels {
    AMOUNT = 'amount',
    PERCENTAGE = 'percentage',
}

const validationSchema = Yup.object({
    [FormFiels.AMOUNT]: Yup.number()
        .nullable()
        .min(0, 'Amount must be greater than or equal to 0')
        .required('Amount is required.'),
    [FormFiels.PERCENTAGE]: Yup.number().nullable().min(0, 'Percentage must be greater than or equal to 0'),
});

const EditAdditionalChargeModal = ({ open, currencySymbol, initialData, onClose, onConfirm }: Props) => {
    const formik = useFormik({
        initialValues: {
            [FormFiels.AMOUNT]: initialData?.amount ?? 0,
            [FormFiels.PERCENTAGE]: initialData?.percentage ?? null,
        },
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values, helpers) => {
            try {
                await onConfirm(values);
            } catch (error) {
                if (isBeValidationError(error) && error.response) {
                    helpers.setErrors(
                        Object.fromEntries(error.response.data.subErrors.map((e) => [e.field, e.message]))
                    );
                } else {
                    Notification({
                        type: 'error',
                        messageTitle: 'Opearation Failed',
                        description: getErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE,
                    });
                }
            }
        },
    });

    return (
        <ConfirmationModal
            title="Edit Additional Charge"
            confirmButtonText="Save"
            icon={<EditOutlined />}
            open={open}
            onConfirm={formik.handleSubmit}
            onCancel={onClose}
            confirmationButtonLoading={formik.isSubmitting}
            confirmationButtonDisabled={formik.isSubmitting}
            content={
                <>
                    <FormikProvider value={formik}>
                        <Space size={16} direction="vertical" style={{ width: '100%' }}>
                            <Typography.Text>
                                Default amounts can be overwritten. This will affect only the specific customer.
                            </Typography.Text>

                            <Row justify="space-between" gutter={6}>
                                <Col span={10}>
                                    <LabelRequiredSymbol />{' '}
                                    <label htmlFor={FormFiels.AMOUNT}>Amount ({currencySymbol}) :</label>
                                </Col>
                                <Col span={14}>
                                    <FormNumberInput
                                        name={FormFiels.AMOUNT}
                                        min={0}
                                        precision={2}
                                        placeholder="Enter Amount"
                                    />
                                </Col>
                            </Row>

                            <Row justify="space-between" gutter={6}>
                                <Col span={10}>
                                    <label htmlFor={FormFiels.PERCENTAGE}>Percentage ({currencySymbol}) : </label>
                                </Col>
                                <Col span={14}>
                                    <FormNumberInput
                                        name={FormFiels.PERCENTAGE}
                                        min={0}
                                        precision={2}
                                        placeholder="Enter Percentage"
                                    />
                                </Col>
                            </Row>
                        </Space>
                    </FormikProvider>
                </>
            }
        />
    );
};

export default EditAdditionalChargeModal;
